import React, { useMemo, createContext, useContext, useState, useEffect } from 'react';
import { Locale } from 'date-fns';
import { InfoContext } from '../../context';

type Value = {
	locale?: Locale;
};

const LocaleContext = createContext<Value>({} as Value);
export const useDateFnsLocaleContext = () => useContext(LocaleContext);

export function DateFnsLocaleProvider({ children }: { children: React.ReactNode }) {
	const [locale, setLocale] = useState<Locale>();
	const infoContext = useContext(InfoContext);

	useEffect(() => {
		const localLang = infoContext.languages.selected;
		const platformLang = localLang?.replace('_', '-');
		const localShort = localLang?.split('_')[0];

		const importLocaleFile = async () => {
			const modules = import.meta.glob('../../../node_modules/date-fns/locale/*/index.js');
			const moduleKeys = Object.keys(modules);
			const langKey =
				moduleKeys.find((keyStr) => keyStr.includes(`/${platformLang}/`)) ||
				moduleKeys.find((keyStr) => keyStr.includes(`/${localShort}/`));

			try {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const localeToSet = (await modules[langKey as string]()) as any;

				setLocale(localeToSet.default);
			} catch (e) {
				console.error({ e });
				// load default en-US
				const localeToSet = await import('date-fns/locale/en-US/index.js');

				setLocale(localeToSet.default);
			}
		};

		if (!locale || locale?.code !== platformLang) {
			importLocaleFile();
		}
	}, [locale, infoContext]);

	const value = useMemo(() => ({ locale }), [locale]);

	return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
